import { FC, useState, MouseEvent } from 'react';
import Cookies from 'js-cookie';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import ViewProductDialog from 'src/components/DialogBox/ViewProductDialog/ViewProductDialog';
import {
  actionButtons,
  apiUrls,
  dataStorageKeywords,
  productDialogTitles,
  productDialogSubtitles,
  productStatus,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { productActivation } from 'src/services/product.service';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { calculateDialogHeight } from 'src/utils/dialog.utils';
import { Product } from 'src/models/Management.interface';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import {
  productInitialValues,
  resetQuantityInitialValues,
} from 'src/utils/initialValues.utils';
import {
  productValidation,
  resetQuantityValidation,
} from 'src/utils/validationScheme.utils';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox';
import TextField from 'src/components/InputFields/TextField';
import UnitField from 'src/components/InputFields/UnitPicker';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import { fetchProductUnits } from 'src/services/enums.service';
import {
  FlexColumnBox,
  FlexContainerBox,
  FlexRowStartBox,
  ResetFlexRowBox,
} from '../DialogBox/POBox/POBox.styles';

const ProductActionMenuButton: FC<{
  rowId: any;
  name: string;
  status: string;
  availableQty: number | string;
  fetchProducts: () => void;
  deleteProduct: (id: string, companyId: string) => void;
}> = ({ rowId, name, status, availableQty, fetchProducts, deleteProduct }) => {
  const { showSnackbar } = StyledSnackbar();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedName, setName] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [unitList, setUnitList] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const fetchUnits = async () => {
    try {
      const enums = await fetchProductUnits();
      setUnitList(enums || []);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditFormSubmit = async () => {
    if (!selectedProduct) {
      return;
    }

    try {
      const response = await axiosTokenInstance.put(apiUrls.EDIT_PRODUCT, {
        id: rowId,
        name: formik.values.name,
        unit: formik.values.unit,
        unitPrice: formik.values.unitPrice,
        displayOrder: formik.values.displayOrder,
      });
      setEditDialogOpen(false);
      fetchProducts();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogOpen = async (product: Product) => {
    fetchUnits();
    setSelectedProduct(product);

    try {
      const response = await axiosTokenInstance.get(apiUrls.GET_PRODUCT_BY_ID, {
        params: { id: rowId },
      });

      formik.setValues({
        name: response.data.name,
        unit: response.data.unit,
        unitPrice: Number(response.data.unitPrice).toFixed(2),
        displayOrder: response.data.displayOrder,
      });
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedProduct(null);
    formik.resetForm();
  };

  const handleResetFormSubmit = async () => {
    try {
      const response = await axiosTokenInstance.put(
        apiUrls.RESET_PRODUCT_QUANTITY,
        {
          productId: rowId,
          quantity: Number(resetFormik.values.quantity),
        }
      );
      setResetDialogOpen(false);
      resetFormik.resetForm();
      fetchProducts();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleResetDialogOpen = () => {
    handleClose();
    resetFormik.resetForm();
    resetFormik.setFieldValue('quantity', availableQty);
    setResetDialogOpen(true);
  };

  const handleResetDialogClose = () => {
    setResetDialogOpen(false);
    resetFormik.resetForm();
  };

  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleView = async () => {
    handleClose();
    try {
      const response = await axiosTokenInstance.get(apiUrls.GET_PRODUCT_BY_ID, {
        params: { id: rowId },
      });
      setSelectedProduct(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedProduct(null);
  };

  const handleDialogOpen = (type: string) => {
    setDialogType(type);
    setName(name);
    setDialogOpen(true);
    handleClose();
  };

  const handleConfirm = async () => {
    if (!selectedName) {
      return;
    }
    const companyId = Cookies.get(dataStorageKeywords.COMPANY_ID);

    try {
      if (dialogType === actionButtons.DELETE) {
        if (rowId && companyId) {
          deleteProduct(rowId, companyId);
        }
        fetchProducts();
      } else if (
        dialogType === actionButtons.ACTIVATE ||
        dialogType === actionButtons.DEACTIVATE
      ) {
        const newStatus = dialogType === actionButtons.ACTIVATE;
        const data = await productActivation(rowId, newStatus);
        showSnackbar(data.message, 'success');
        fetchProducts();
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
      setName(null);
    }
  };

  const formik = useFormik({
    initialValues: productInitialValues,
    validationSchema: productValidation,
    onSubmit: handleEditFormSubmit,
  });

  const resetFormik = useFormik({
    initialValues: resetQuantityInitialValues,
    validationSchema: resetQuantityValidation,
    onSubmit: handleResetFormSubmit,
  });

  const getDialogHeight = () =>
    calculateDialogHeight(
      formik,
      [
        ['name', 'code'],
        ['unit', 'unitPrice'],
      ],
      500
    );

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={handleView}
            iconClassName="bx bx-file-find"
            text="View"
          />
          <MenuItemWithIcon
            onClick={() =>
              handleEditDialogOpen({
                id: rowId,
                displayOrder: Number(null),
                name: name,
                unit: '',
                unitPrice: '',
                availableQty: 0,
                isActive: status === productStatus.ACTIVE,
              })
            }
            iconClassName="bx bx-edit"
            text="Edit"
          />
          <CustomDialog
            height={`${getDialogHeight()}px`}
            open={editDialogOpen}
            handleClose={handleEditDialogClose}
            title={productDialogTitles.EDIT}
            subtitle={productDialogSubtitles.EDIT}
            onSubmit={formik.handleSubmit}
            smScreen={false}
            buttonLabel="Update"
          >
            {selectedProduct && (
              <FlexContainerBox>
                <FlexRowStartBox>
                  <FlexColumnBox>
                    <TextField
                      id="name"
                      label="Name"
                      formikProps={formik}
                      width="294px"
                    />
                  </FlexColumnBox>
                  <FlexColumnBox>
                    <TextField
                      id="displayOrder"
                      label="Display Order"
                      formikProps={formik}
                      width="294px"
                    />
                  </FlexColumnBox>
                </FlexRowStartBox>
                <FlexRowStartBox>
                  <FlexColumnBox>
                    <UnitField
                      id="unit"
                      label="Unit"
                      formikProps={formik}
                      unitList={unitList}
                      width="294px"
                    />
                  </FlexColumnBox>
                  <FlexColumnBox>
                    <TextField
                      id="unitPrice"
                      placeholder="00.00"
                      label="Unit Price"
                      formikProps={formik}
                      width="294px"
                    />
                  </FlexColumnBox>
                </FlexRowStartBox>
              </FlexContainerBox>
            )}
          </CustomDialog>
          <MenuItemWithIcon
            onClick={() =>
              handleDialogOpen(
                status === productStatus.ACTIVE
                  ? actionButtons.DEACTIVATE
                  : actionButtons.ACTIVATE
              )
            }
            iconClassName={
              status === productStatus.ACTIVE
                ? 'bx bx-minus-circle'
                : 'bx bx-radio-circle-marked'
            }
            text={status === productStatus.ACTIVE ? 'Deactivate' : 'Activate'}
          />
          <MenuItemWithIcon
            onClick={handleResetDialogOpen}
            iconClassName="bx bx-reset"
            text="Reset Quantity"
          />
          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.DELETE)}
            iconClassName="bx bx-trash"
            text="Delete"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>
      <ConfirmationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={
          dialogType === actionButtons.DELETE
            ? productDialogTitles.DELETE
            : dialogType === actionButtons.ACTIVATE
            ? productDialogTitles.ACTIVATE
            : productDialogTitles.DEACTIVATE
        }
        subtitle={
          dialogType === actionButtons.DELETE
            ? productDialogSubtitles.DELETE
            : dialogType === actionButtons.ACTIVATE
            ? productDialogSubtitles.ACTIVATE
            : productDialogSubtitles.DEACTIVATE
        }
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : dialogType === actionButtons.ACTIVATE
            ? 'var(--success)'
            : 'var(--error)'
        }
        alertIconColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : dialogType === actionButtons.ACTIVATE
            ? 'var(--warning)'
            : 'var(--error)'
        }
      />

      <ViewProductDialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        data={selectedProduct}
      />

      <CustomDialog
        height="350px"
        width="650px"
        open={resetDialogOpen}
        handleClose={handleResetDialogClose}
        title={productDialogTitles.RESET}
        subtitle={productDialogSubtitles.RESET}
        onSubmit={resetFormik.handleSubmit}
        smScreen={false}
        buttonLabel="Reset"
        showCancelButton={true}
      >
        <FlexContainerBox>
          <ResetFlexRowBox>
            <FlexColumnBox>
              <TextField
                id="quantity"
                label="Available Quantity"
                formikProps={resetFormik}
                width="294px"
              />
            </FlexColumnBox>
          </ResetFlexRowBox>
        </FlexContainerBox>
      </CustomDialog>
    </>
  );
};
export default ProductActionMenuButton;
